import { Tooltip as T } from "radix-ui";
import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Kbd } from "./kbd";

export const TooltipProvider = T.Provider;

export interface TooltipProps {
  content: ReactNode;
  /** Optional shortcut shown in the tooltip, e.g. "mod+k" or "g c". */
  shortcut?: string;
  side?: "top" | "right" | "bottom" | "left";
  align?: "start" | "center" | "end";
  delay?: number;
  children: ReactNode;
  disabled?: boolean;
  className?: string;
}

/**
 * Short hint on hover/focus. Never put essential information only in a tooltip.
 *   <Tooltip content="Search" shortcut="mod+k"><IconButton aria-label="Search">…</IconButton></Tooltip>
 */
export function Tooltip({ content, shortcut, side = "top", align = "center", delay, children, disabled, className }: TooltipProps) {
  if (disabled || (!content && !shortcut)) return <>{children}</>;
  return (
    <T.Root delayDuration={delay}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          align={align}
          sideOffset={6}
          collisionPadding={8}
          className={cn(
            "z-[60] flex max-w-72 items-center gap-2 rounded-item bg-navy px-2.5 py-1.5 text-xs font-semibold text-paper shadow-md data-[state=delayed-open]:animate-fade-in data-[state=closed]:animate-fade-out dark:bg-surface-raised dark:text-fg dark:ring-1 dark:ring-line",
            className,
          )}
        >
          <span>{content}</span>
          {shortcut && <Kbd combo={shortcut} className="[&_kbd]:border-white/15 [&_kbd]:bg-white/10 [&_kbd]:text-paper/80 [&_kbd]:shadow-none dark:[&_kbd]:border-line-strong dark:[&_kbd]:bg-surface dark:[&_kbd]:text-fg-muted" />}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}
